import type { MathJsInstance, MathNode } from 'mathjs';

export type ExprToken =
  | { type: 'number'; value: string }
  | { type: 'operator'; value: string } // + - * / ( )
  | { type: 'variable'; slug: string; label?: string }
  | { type: 'whitespace'; value: string };

export type ExprTreeResult =
  | {
      ok: true;
      expression: string;
      ast: MathNode;
      tokens: ExprToken[];

      normalized: string;
      variables: string[];
    }
  | {
      ok: false;
      expression: string;
      error: string;
      tokens: ExprToken[];
    };

type AvailableVar = { slug: string; label?: string };

export async function buildMathTreeFromRawString(args: {
  expression: string;
  availableVariables: AvailableVar[];
}): Promise<ExprTreeResult> {
  const tokens = tokenizeRawExpression(args.expression);
  let expression: string;
  try {
    expression = tokensToMathJsExpression(tokens);
  } catch (err) {
    return {
      ok: false,
      expression: args.expression,
      error: err instanceof Error ? err.message : String(err),
      tokens,
    };
  }

  // collect identifier-like tokens (variable tokens)
  const usedSymbols = Array.from(new Set(tokens.filter((t) => t.type === 'variable').map((t) => t.slug)));

  const known = new Set(args.availableVariables.map((v) => v.slug));
  const variables: string[] = [];
  const unknown: string[] = [];

  for (const s of usedSymbols) {
    if (known.has(s)) variables.push(s);
    else unknown.push(s);
  }

  if (unknown.length > 0) {
    return {
      ok: false,
      expression,
      error: `Unknown variables in formula: ${unknown.join(', ')}`,
      tokens,
    };
  }

  try {
    const math = await importMathJs();
    const ast = math.parse(expression);

    const normalized = ast.toString();

    return {
      ok: true,
      expression,
      ast,
      tokens,
      normalized,
      variables,
    };
  } catch (err) {
    return {
      ok: false,
      expression,
      error: err instanceof Error ? err.message : String(err),
      tokens,
    };
  }
}

export function tokenizeRawExpression(expression: string): ExprToken[] {
  return coalesceWhitespace(tokenizeText(expression));
}

type MathJsESM = typeof import('mathjs');
type MathJsCJS = { default: MathJsInstance };
type MathJsImport = MathJsESM | MathJsCJS | MathJsInstance;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

export async function importMathJs(): Promise<MathJsInstance> {
  const mod = (await import('mathjs')) as unknown as MathJsImport;

  if (isRecord(mod) && 'create' in mod && 'all' in mod) {
    const create = (mod as MathJsESM).create;
    const all = (mod as MathJsESM).all;
    return create(all);
  }

  if (isRecord(mod) && 'default' in mod) {
    return (mod as MathJsCJS).default;
  }

  return mod as MathJsInstance;
}

export function tokensToMathJsExpression(tokens: ExprToken[]): string {
  const parts: string[] = [];
  let prevNonWhitespace: ExprToken | null = null;

  const isValueLike = (t: ExprToken) =>
    t.type === 'number' || t.type === 'variable' || (t.type === 'operator' && t.value === ')');
  const isValueStart = (t: ExprToken) =>
    t.type === 'number' || t.type === 'variable' || (t.type === 'operator' && t.value === '(');

  for (const t of tokens) {
    if (t.type === 'whitespace') continue;
    if (prevNonWhitespace && isValueLike(prevNonWhitespace) && isValueStart(t)) {
      throw new Error('Missing operator between terms.');
    }
    prevNonWhitespace = t;
  }

  for (const t of tokens) {
    if (t.type === 'whitespace') parts.push(t.value);
    else if (t.type === 'number') parts.push(t.value);
    else if (t.type === 'operator') {
      if (/[A-Za-z_]/.test(t.value)) {
        throw new Error(`Invalid character in expression: "${t.value}"`);
      }
      parts.push(t.value);
    } else if (t.type === 'variable') parts.push(t.slug);
  }

  const expr = parts.join('').replace(/\s+/g, ' ').trim();

  const bad = expr.match(/[^0-9A-Za-z_\s+\-*/().,]/);
  if (bad) {
    const ch = bad[0];
    if (ch === '^') {
      throw new Error('Unsupported operator: "^". Use + - * / ( )');
    }
    throw new Error(`Invalid character in expression: "${ch}"`);
  }

  return expr;
}

function tokenizeText(text: string): ExprToken[] {
  const tokens: ExprToken[] = [];
  let i = 0;

  const pushWs = (s: string) => s && tokens.push({ type: 'whitespace', value: s });
  const pushOp = (ch: string) => tokens.push({ type: 'operator', value: ch });
  const isDigit = (ch: string) => ch >= '0' && ch <= '9';

  while (i < text.length) {
    const ch = text[i] ?? '';

    if (/\s/.test(ch)) {
      let j = i + 1;
      while (j < text.length && /\s/.test(text[j] ?? '')) j++;
      pushWs(text.slice(i, j));
      i = j;
      continue;
    }

    if (/[+\-*/()]/.test(ch)) {
      pushOp(ch);
      i += 1;
      continue;
    }

    if (isDigit(ch) || ch === '.') {
      let j = i;

      if (text[j] === '.') {
        if (!isDigit(text[j + 1] ?? '')) {
          // disallow bare '.' / property access
          tokens.push({ type: 'operator', value: '.' });
          i += 1;
          continue;
        }
        j++;
      }

      while (j < text.length && isDigit(text[j] ?? '')) j++;

      if (text[j] === '.') {
        j++;
        while (j < text.length && isDigit(text[j] ?? '')) j++;
      }

      tokens.push({ type: 'number', value: text.slice(i, j) });
      i = j;
      continue;
    }

    // |VARIABLE or $VARIABLE$/$VARIABLE (legacy)
    if (ch === '|') {
      let j = i + 1;
      while (j < text.length && /[A-Za-z0-9_-]/.test(text[j] ?? '')) j++;

      if (j > i + 1) {
        const slug = text.slice(i + 1, j);
        tokens.push({ type: 'variable', slug });
        i = j;
        continue;
      }

      // unmatched |, treat as operator
      tokens.push({ type: 'operator', value: ch });
      i += 1;
      continue;
    }

    // $VARIABLE or $VARIABLE$
    if (ch === '$') {
      let j = i + 1;
      while (j < text.length && /[A-Za-z0-9_-]/.test(text[j] ?? '')) j++;

      if (j > i + 1) {
        const slug = text.slice(i + 1, j);
        tokens.push({ type: 'variable', slug });
        i = text[j] === '$' ? j + 1 : j;
        continue;
      }

      // unmatched $, treat as operator
      tokens.push({ type: 'operator', value: ch });
      i += 1;
      continue;
    }

    tokens.push({ type: 'operator', value: ch });
    i += 1;
  }

  return tokens;
}

function coalesceWhitespace(tokens: ExprToken[]): ExprToken[] {
  const out: ExprToken[] = [];
  for (const t of tokens) {
    if (t.type === 'whitespace') {
      const prev = out[out.length - 1];
      if (prev?.type === 'whitespace') prev.value += t.value;
      else out.push({ ...t });
    } else {
      out.push(t);
    }
  }
  return out;
}
